import Component from 'core/Component';
import { deepMerge } from 'toolbox/deepMerge';
import Registry from 'core/ComponentRegistry';
import { on, off, trigger } from 'toolbox/event';
import { Event } from 'services/EventEmitter';

/* eslint complexity: ["error", {"max": 10}] */

/**
 * This is a description of the LocalizationMenu constructor function.
 * @class
 * @classdesc This component is needed to manage currency and language options/blocks on country change.
 * @extends Component
 */
export default class LocalizationMenu extends Component {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            countriesConfig: null, // config with combinations of allowed currencies, languages of each country
            currentCountryCode: null,
            currentCurrencyCode: null,
            currentLanguageCode: null,
        }, options));
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.countryField = this.element.querySelector('select[name="country"]');
        this.selectors.currencyComponent = this.element.querySelector('select[name="currency"]').closest('[data-js-field-component]');
        this.selectors.languageComponent = this.element.querySelector('select[name="language"]').closest('[data-js-field-component]');
        this.selectors.currencyBlock = this.element.querySelector('[data-currency-block]');
        this.selectors.languageBlock = this.element.querySelector('[data-language-block]');
        this.selectors.oneLanguageBlock = this.element.querySelector('[data-onelanguage-block]');
        this.selectors.oneCurrencyBlock = this.element.querySelector('[data-onecurrency-block]');
        this.selectors.oneCurrencyLabel = this.element.querySelector('[data-onecurrency-label]');
        this.selectors.oneLanguageLabel = this.element.querySelector('[data-onelanguage-label]');

        this.classes = {
            hidden: 'h-hidden',
        };
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        on('change.localizationmenu', this.selectors.countryField, this.onChange.bind(this));
        Event.on('tooltip.hide.content', this.onTooltipHide, this);
        Event.on('hamburger.submenu.close', this.onHamburgerSubmenuClose, this);
    }

    /**
     * After init
     * Run any script after the component is fully initialized
     */
    afterInit() {
        Registry.registerChildren(this.element);
    }

    /**
     * Call Reset method after tooltip is hide
     * @param {String} id parent component ID
     */
    onTooltipHide(id) {
        if (!this.element || !this.element.closest(`#${id}`)) {
            return;
        }

        this.resetFields();
    }

    /**
     * Call Reset method after Hamburger submenu is closed
     * @param {String} id parent component ID
     */
    onHamburgerSubmenuClose(id) {
        if (!this.element || !this.element.closest(`[data-component-id="${id}"]`)) {
            return;
        }

        this.resetFields();
    }

    /**
     * Reset localization fields to default state
     * @param {String} id parent component ID
     */
    resetFields() {
        this.selectors.countryField.value = this.options.currentCountryCode;

        trigger('change', this.selectors.countryField, {
            bubbles: true,
        });
    }

    /**
     * Country field change event handler
     * Update currency and language options for selected country
     * @param {Event} event event object
     */
    onChange(event) {
        const { target: { value } } = event;
        const { options: { countriesConfig, currentCurrencyCode, currentLanguageCode } } = this;
        const countryObj = countriesConfig && countriesConfig.countries[value];

        const currencyComponent = Registry.getComponent(this.selectors.currencyComponent);
        const languageComponent = Registry.getComponent(this.selectors.languageComponent);

        if (!countryObj || !currencyComponent || !languageComponent) {
            return;
        }

        const currencyOptions = Object.keys(countriesConfig.currencyLabels)
            .filter(currencyCode => !countryObj.currencies || countryObj.currencies.indexOf(currencyCode) !== -1)
            .map(currencyCode => ({
                val: currencyCode,
                label: countriesConfig.currencyLabels[currencyCode],
                selected: currentCurrencyCode === currencyCode,
            }));
        const languageOptions = countryObj.languages
            .map(languageCode => ({
                val: languageCode,
                label: countriesConfig.languageLabels[languageCode],
                selected: currentLanguageCode === languageCode,
            }), this);
        currencyComponent.updateOptions(currencyOptions);
        languageComponent.updateOptions(languageOptions);

        if (countryObj.remote || countriesConfig.isEditorialMode) {
            this.selectors.oneCurrencyBlock.classList.add(this.classes.hidden);
            this.selectors.currencyBlock.classList.add(this.classes.hidden);
        } else if (currencyOptions.length === 1) {
            this.selectors.oneCurrencyBlock.classList.remove(this.classes.hidden);
            this.selectors.oneCurrencyLabel.innerHTML = currencyOptions[0].label;
            this.selectors.currencyBlock.classList.add(this.classes.hidden);
        } else {
            this.selectors.oneCurrencyBlock.classList.add(this.classes.hidden);
            this.selectors.currencyBlock.classList.remove(this.classes.hidden);
        }

        if (languageOptions.length === 1) {
            this.selectors.oneLanguageBlock.classList.remove(this.classes.hidden);
            this.selectors.oneLanguageLabel.innerHTML = languageOptions[0].label;
            this.selectors.languageBlock.classList.add(this.classes.hidden);
        } else {
            this.selectors.oneLanguageBlock.classList.add(this.classes.hidden);
            this.selectors.languageBlock.classList.remove(this.classes.hidden);
        }
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        off('change.localizationmenu', this.selectors.countryField);
        Event.removeListener('tooltip.hide.content', this.onTooltipHide);
        Event.removeListener('hamburger.submenu.toggle', this.onHamburgerSubmenuClose);
    }
}
